import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import { useRetroRealtime } from '../../hooks/useRetroRealtime';
import CardInput from './CardInput';
import RetroCard from './RetroCard';

const PHASE_META = {
  start:    { label: 'Start Doing', emoji: '🟢' },
  stop:     { label: 'Stop Doing',  emoji: '🔴' },
  continue: { label: 'Continue',    emoji: '🔵' },
  kudos:    { label: 'Kudos Wall',  emoji: '🏆' },
};

const NON_WRITING_PHASES = new Set(['lobby', 'discussion', 'voting', 'complete']);

function getPhaseLabel(phase) {
  if (PHASE_META[phase]) return PHASE_META[phase].label;
  // Capitalise first letter of custom phase slug
  return phase.charAt(0).toUpperCase() + phase.slice(1);
}

function getPhaseEmoji(phase) {
  return PHASE_META[phase]?.emoji ?? '⬜';
}

export default function AllInOneBoard({
  sessionId,
  session,
  phases,
  isFacilitator,
  myParticipantId,
  isLocked,
  onAdvance,
}) {
  const [cards, setCards] = useState([]);
  const [isAnon, setIsAnon] = useState(() => {
    try {
      return localStorage.getItem(`retro_anon_${sessionId}`) === 'true';
    } catch {
      return false;
    }
  });

  // Only show writing phases (exclude lobby/discussion/voting/complete)
  const writingPhases = (phases || []).filter(p => !NON_WRITING_PHASES.has(p));

  const loadCards = useCallback(async () => {
    try {
      const data = await api.sessions.get(sessionId);
      setCards(data.cards || []);
    } catch (e) {
      console.error('AllInOneBoard: failed to load cards', e);
    }
  }, [sessionId]);

  // Initial load
  useEffect(() => {
    loadCards();
  }, [loadCards]);

  // Realtime subscription — refetch on any card change
  useRetroRealtime(sessionId, { onCards: loadCards });

  const handleAnonToggle = () => {
    setIsAnon(prev => {
      const next = !prev;
      try { localStorage.setItem(`retro_anon_${sessionId}`, String(next)); } catch {}
      return next;
    });
  };

  const handleCardSubmitted = (card) => {
    setCards(prev => [...prev, card]);
  };

  const handleCardUpdated = (updated) => {
    setCards(prev => prev.map(c => c.id === updated.id ? updated : c));
  };

  const handleCardDeleted = (id) => {
    setCards(prev => prev.filter(c => c.id !== id));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Top toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
          All-in-One Mode — add cards to any column
        </span>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={isAnon}
            onChange={handleAnonToggle}
            style={{ cursor: 'pointer' }}
          />
          Post anonymously
        </label>
      </div>

      {/* Columns */}
      <div style={{
        display: 'flex',
        gap: 16,
        overflowX: 'auto',
        padding: 16,
        flex: 1,
        alignItems: 'flex-start',
      }}>
        {writingPhases.map(phase => {
          const phaseCards = cards.filter(c => c.phase === phase);
          const isKudos = phase === 'kudos';

          return (
            <div
              key={phase}
              style={{
                minWidth: 280,
                flex: 1,
                background: 'var(--bg-elevated)',
                borderRadius: 8,
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {/* Column header */}
              <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>
                {getPhaseEmoji(phase)} {getPhaseLabel(phase)}
                <span
                  style={{
                    marginLeft: 8,
                    fontWeight: 400,
                    fontSize: '0.8rem',
                    color: 'var(--text-muted)',
                  }}
                >
                  ({phaseCards.length})
                </span>
              </div>

              {/* Card input */}
              {!isLocked && (
                <CardInput
                  sessionId={sessionId}
                  participantId={myParticipantId}
                  phase={phase}
                  isKudos={isKudos}
                  onSubmitted={handleCardSubmitted}
                />
              )}

              {/* Card list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {phaseCards.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '12px 0' }}>
                    No cards yet
                  </div>
                ) : (
                  phaseCards.map(card => (
                    <RetroCard
                      key={card.id}
                      card={card}
                      phase={phase}
                      isFacilitator={isFacilitator}
                      myParticipantId={myParticipantId}
                      isRevealed={true}
                      isLocked={isLocked}
                      selectedForMerge={false}
                      onSelectForMerge={() => {}}
                      onUpdated={handleCardUpdated}
                      onDeleted={handleCardDeleted}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}

        {writingPhases.length === 0 && (
          <div style={{ color: 'var(--text-muted)', padding: 24 }}>
            No writing phases configured for this format.
          </div>
        )}
      </div>

      {/* Facilitator advance button */}
      {isFacilitator && (
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}>
          <button
            className="btn btn-primary"
            onClick={onAdvance}
            style={{ fontWeight: 600 }}
          >
            ✅ Done — Move to Discussion
          </button>
        </div>
      )}
    </div>
  );
}
